import React, { useEffect, useState, forwardRef } from "react";
import { Country } from "@shared/interfaces/Country";
import { useFormContext } from "react-hook-form";
import useFetchCountries from "@pages/Project/utils/useFetchCountries";
import Loader from "@shared/components/Loader";

interface CountryDropdownProps {
  onSelect: (id: number, name: string) => void;
  selectedCountry?: { id: number; name: string };
  isEditing?: boolean;
  customInputStyle?: string;
}

// ✅ Wrap component with forwardRef
const CountryDropdown = forwardRef<HTMLInputElement, CountryDropdownProps>(
  ({ onSelect, selectedCountry, isEditing, customInputStyle }, ref) => {
    const { setValue, trigger } = useFormContext();
    const [query, setQuery] = useState("");
    const [filteredCountries, setFilteredCountries] = useState<Country[]>([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const { countries, isCountriesDataLoading } = useFetchCountries();

    useEffect(() => {
      if (query && countries.length > 0) {
        const results = countries.filter((country) =>
          country.name.toLowerCase().includes(query.toLowerCase())
        );
        setFilteredCountries(results);
      } else {
        setFilteredCountries([]);
      }
    }, [query]);

    useEffect(() => {
      if (selectedCountry?.name && countries.length > 0) {
        setQuery(selectedCountry.name);
      } else {
        setQuery("");
      }
    }, [selectedCountry, countries]);

    const handleSelect = (id: number, name: string) => {
      setQuery(name);
      setIsDropdownOpen(false);
      onSelect(id, name);
      setValue("countryId", id, { shouldValidate: true });
      setValue("countryName", name, { shouldValidate: true });
      trigger("countryName");
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(e.target.value);
      setIsDropdownOpen(true);
    };

    const handleFocus = () => {
      setIsDropdownOpen(true);
    };

    const handleBlur = () => {
      setTimeout(() => {
        setIsDropdownOpen(false);
        if (!query.trim()) {
          onSelect(-2, "");
          setValue("countryId", 0);
          setValue("countryName", "");
          trigger("countryName");
        }
      }, 200);
    };

    return (
      <div className="relative text-sm 2xl:text-base">
        <input
          ref={ref} // ✅ Assign the ref here
          type="text"
          value={query || ""}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={`w-full p-2 border rounded-md text-xs sm:text-sm ${
            isEditing
              ? "border-gray-300 focus:outline-none focus:border-blue-500"
              : "border-gray-300 text-slate-400"
          } focus:outline-none ${customInputStyle}`}
          placeholder="Start typing country name..."
          readOnly={!isEditing}
        />
        {isDropdownOpen && filteredCountries.length > 0 && isEditing && (
          <ul className="absolute z-[99] bg-white border border-gray-300 rounded-md mt-1 max-h-48 w-full overflow-auto">
            {filteredCountries.map((country) => (
              <li
                key={country.id}
                onClick={() => handleSelect(country.id, country.name)}
                className="p-2 cursor-pointer hover:bg-blue-100 flex justify-between"
              >
                <span>{country.name}</span>
              </li>
            ))}
          </ul>
        )}
        {isCountriesDataLoading && (
          <div className="absolute top-1 right-0 p-2">
            <span>
              <Loader color="#AAA" />
            </span>
          </div>
        )}
      </div>
    );
  }
);

export default CountryDropdown;
